/**
 * Sock Upsell
 *
 * Reveals the sock upsell panel in the cart drawer once the cart reaches the
 * free shipping threshold. The "Ota 3 maksa 2" discount is always on in
 * Shopify admin — this only surfaces it at the moment the customer just
 * earned free shipping and is least price-sensitive.
 *
 * Shows once per session (sessionStorage), same pattern as exit-intent.
 * Panel markup lives in snippets/sock-upsell-popup.liquid.
 */
(function() {
  'use strict';

  // Two surfaces, two lifetimes:
  //   - the drawer panel stays available for as long as the offer applies, and
  //     only disappears when the customer actively dismisses it
  //   - the modal interrupts once per session and never returns
  var PANEL_DISMISSED_KEY = 'sockUpsellDismissed';
  var MODAL_SHOWN_KEY = 'sockUpsellModalShown';
  var SELECTOR = '[data-sock-upsell]';
  var MODAL_SELECTOR = '[data-sock-modal]';
  var PAIRS_FOR_DISCOUNT = 3;

  function flagSet(key) {
    try {
      return sessionStorage.getItem(key) === 'true';
    } catch (e) {
      // Private browsing can throw on sessionStorage access — treat as already
      // handled so we fail closed rather than nagging on every cart update.
      return true;
    }
  }

  function setFlag(key) {
    try {
      sessionStorage.setItem(key, 'true');
    } catch (e) {
      /* no-op */
    }
  }

  function hidePanel(panel) {
    panel.hidden = true;
    panel.setAttribute('hidden', '');
    panel.style.display = 'none';
  }

  function showPanel(panel) {
    panel.hidden = false;
    panel.removeAttribute('hidden');
    panel.style.display = '';
  }

  /**
   * Opens the quick-add modal so the customer can pick a size before adding.
   * Socks always have a size option, so adding blind would pick the wrong one.
   * Mirrors the approach in cart-recommendations.js (each feature keeps its
   * own copy — they are independent IIFEs).
   */
  function openQuickAdd(productUrl, button) {
    var originalText = button.innerHTML;
    button.innerHTML = '...';
    button.disabled = true;

    function restore() {
      button.innerHTML = originalText;
      button.disabled = false;
    }

    fetch(productUrl)
      .then(function(response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var productGrid = doc.querySelector('[data-product-grid-content]');
        var modalContent = document.getElementById('quick-add-modal-content');
        var dialog = document.getElementById('quick-add-dialog');

        if (!productGrid || !modalContent || !dialog) {
          window.location.href = productUrl;
          return;
        }

        modalContent.innerHTML = productGrid.innerHTML;

        if (typeof dialog.showDialog === 'function') {
          dialog.showDialog();
        }

        // showDialog defers the open to requestAnimationFrame, so check the
        // result a frame later. showModal() is rejected while another dialog
        // already occupies the top layer — most often the cookie consent
        // banner, which a first-time visitor has not dismissed yet. Falling
        // back to the product page is better than a button that does nothing.
        // showDialog() opens inside its own requestAnimationFrame, so the
        // result is only observable one frame after that — hence the nesting.
        requestAnimationFrame(function() {
          requestAnimationFrame(function() {
            restore();

            var inner = dialog.querySelector('dialog');
            if (!inner || !inner.hasAttribute('open')) {
              window.location.href = productUrl;
            }
          });
        });
      })
      .catch(function() {
        window.location.href = productUrl;
      });
  }

  // Delegated from document rather than bound per button. The panel sits inside
  // the `cart-drawer-inner` hydration key, so every cart change morphs it: the
  // node survives but `data-loaded` is reset, which made a re-bind attach a
  // SECOND listener to the same button. Two listeners meant one click opened
  // the quick-add modal and immediately reopened it, tearing down the first.
  // Delegation binds once and survives every morph.
  document.addEventListener('click', function(event) {
    var panelClose = event.target.closest('[data-sock-upsell-close]');
    if (panelClose) {
      var panel = panelClose.closest('[data-sock-upsell]');
      if (panel) {
        hidePanel(panel);
        // Dismissing hides the panel for the session. Merely seeing it does
        // not — the offer stays in the drawer for as long as it applies.
        setFlag(PANEL_DISMISSED_KEY);
      }
      return;
    }

    var modalClose = event.target.closest('[data-sock-modal-close]');
    if (modalClose) {
      closeModal();
      return;
    }

    var addButton = event.target.closest('[data-sock-upsell-add]');
    if (addButton) {
      // Close the offer modal first when the click came from inside it —
      // stacking the quick-add modal on top would fight over the focus trap.
      if (addButton.closest(MODAL_SELECTOR)) closeModal();
      openQuickAdd(addButton.dataset.productUrl, addButton);
    }
  });

  function getModal() {
    return document.querySelector(MODAL_SELECTOR);
  }

  function closeModal() {
    var modal = getModal();
    if (modal && modal.open) modal.close();
  }

  /**
   * Counts how many sock pairs the cart holds. The modal renders once at page
   * load, so it cannot ask Liquid — the handles of the `miesten-sukat`
   * collection travel with it as JSON and are matched against /cart.js.
   */
  function countSockPairs(cart, modal) {
    var handles;
    try {
      handles = JSON.parse(modal.dataset.sockHandles || '[]');
    } catch (e) {
      return 0;
    }
    if (!handles.length) return 0;

    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (handles.indexOf(cart.items[i].handle) !== -1) {
        total += cart.items[i].quantity;
      }
    }
    return total;
  }

  function maybeOpenModal(cart) {
    var modal = getModal();
    if (!modal || modal.open) return;
    if (flagSet(MODAL_SHOWN_KEY)) return;

    var threshold = parseInt(modal.dataset.threshold, 10) || 4000;
    if (cart.total_price < threshold) return;

    // Someone already holding three pairs is using the offer — no interruption.
    if (countSockPairs(cart, modal) >= PAIRS_FOR_DISCOUNT) return;

    setFlag(MODAL_SHOWN_KEY);
    modal.showModal();
  }

  // Backdrop click closes. A click on the dialog element itself only reaches
  // here when it landed outside .sock-modal__inner, i.e. on the backdrop.
  document.addEventListener('click', function(event) {
    var modal = getModal();
    if (modal && modal.open && event.target === modal) closeModal();
  });

  /**
   * Reads the live cart once and drives both surfaces from it.
   *
   * The modal is checked first and independently of the panel: the panel only
   * exists once the cart drawer has hydrated, but crossing the threshold from
   * a product page must still interrupt, and the modal lives in the top-level
   * DOM precisely so it can.
   */
  function checkThreshold() {
    var modal = getModal();
    var panel = document.querySelector(SELECTOR);
    if (!modal && !panel) return;

    // Nothing to do if the modal is spent and the panel is gone or dismissed.
    var panelPending = panel && !flagSet(PANEL_DISMISSED_KEY);
    var modalPending = modal && !flagSet(MODAL_SHOWN_KEY) && !modal.open;
    if (!panelPending && !modalPending) return;

    var thresholdSource = panel || modal;
    var threshold = parseInt(thresholdSource.dataset.threshold, 10) || 4000;

    fetch(window.Theme && window.Theme.routes && window.Theme.routes.cart_url
      ? window.Theme.routes.cart_url + '.js'
      : '/cart.js')
      .then(function(response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function(cart) {
        if (modalPending) maybeOpenModal(cart);

        // Morph re-renders the panel hidden on every cart change, so this
        // re-reveals it each time. Unlike the modal it is not once-per-session:
        // the offer belongs in the drawer for as long as it applies.
        if (panelPending && cart.total_price >= threshold) showPanel(panel);
      })
      .catch(function() {
        /* cart fetch failed — leave both surfaces as they are */
      });
  }

  checkThreshold();

  // Poll for the panel — hydration and morphing can bypass MutationObserver
  var pollInterval = setInterval(checkThreshold, 500);
  setTimeout(function() {
    clearInterval(pollInterval);
  }, 60000);

  // cart:update fires before morph completes, so wait for the new DOM
  document.addEventListener('cart:update', function() {
    setTimeout(checkThreshold, 100);
  });

  // Opening the drawer re-hydrates the section without firing cart:update
  var observer = new MutationObserver(function(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].attributeName === 'open' && mutations[i].target.hasAttribute('open')) {
        setTimeout(checkThreshold, 200);
        break;
      }
    }
  });

  function observeDrawer() {
    var dialog = document.querySelector('.cart-drawer__dialog');
    if (dialog) {
      observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    } else {
      setTimeout(observeDrawer, 1000);
    }
  }
  observeDrawer();
})();
